import { IUser } from '../interfaces';
import {
  isReferralPatient,
  isReadyToBook,
  isRegisteredAwaitingForm,
} from './patientStatus';

export type PriorityLevel = 'P1' | 'P2' | 'P3';
export type PriorityFilter = PriorityLevel | null;

export const PRIORITY_LEVELS: PriorityLevel[] = ['P1', 'P2', 'P3'];

const PRIORITY_SORT_ORDER: Record<string, number> = { P1: 1, P2: 2, P3: 3 };

export enum StatusRank {
  New = 1,
  Registered,
  Invited,
  Initiated,
  NoStatus,
  Migrated,
  Fallback,
  AdminPatient,
}

export function excludeDemoUsers(users: IUser[]): IUser[] {
  return users.filter((u) => u.signupType !== 'demo');
}

export function countByPriority(users: IUser[]): Record<PriorityLevel, number> {
  const counts: Record<PriorityLevel, number> = { P1: 0, P2: 0, P3: 0 };
  for (const u of users) {
    if (u.priority && u.priority in counts) {
      counts[u.priority] += 1;
    }
  }
  return counts;
}

export function getStatusRank(user: IUser): StatusRank {
  if (user.signupType === 'adminWithPatientAccess')
    return StatusRank.AdminPatient;
  if (isReadyToBook(user)) return StatusRank.New;
  if (isRegisteredAwaitingForm(user)) return StatusRank.Registered;
  if (isReferralPatient(user) && !user.onboardingCompleted)
    return StatusRank.Invited;
  if (user.signupType === 'selfSignup' && !user.onboardingCompleted)
    return StatusRank.Initiated;
  if (user.appointmentCreated) return StatusRank.NoStatus;
  if (user.signupType === 'legacy' && !user.onboardingCompleted)
    return StatusRank.Migrated;
  return StatusRank.Fallback;
}

export function filterAndSortUsers(
  users: IUser[],
  priorityFilter: PriorityFilter
): IUser[] {
  const filtered = priorityFilter
    ? users.filter((u) => u.priority === priorityFilter)
    : users;

  return [...filtered].sort((a, b) => {
    const rankA = getStatusRank(a);
    const rankB = getStatusRank(b);

    if (rankA !== rankB) return rankA - rankB;

    const prioA = PRIORITY_SORT_ORDER[a.priority ?? ''] ?? Infinity;
    const prioB = PRIORITY_SORT_ORDER[b.priority ?? ''] ?? Infinity;
    if (prioA !== prioB) return prioA - prioB;

    if (rankA === StatusRank.Fallback || rankA === StatusRank.NoStatus) {
      return (a.firstName || '').localeCompare(b.firstName || '', undefined, {
        sensitivity: 'base',
      });
    }

    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return timeB - timeA;
  });
}
